import { useMemo } from "react";
import { 
  isSecurityTradeable,
  hasSecurityTag,
  isSecurityComplex,
  isSecurityNonComplex,
  hasEnoughCash,
  isFutureOrTodayDate,
  shouldShowCallToBrokerForSecType,
  isNonUCITSSecurity,
  isProfOnlySecurity,
  SEC_TYPE_10,
  SEC_TYPE_11,
  SUBSCR_PORTAL_GROUP,
} from "utils/trading/tradingPermissions";
import { 
  ButtonConfig, 
  Security, 
  PageContext, 
  ButtonAction,
  ButtonVariant,
  SecurityTypeCode,
  TradingType,
} from "utils/trading/types";
import { useModifiedTranslation } from "./useModifiedTranslation";
import { useTradingContext } from "./useTradingContext";

interface UseTradingButtonsParams {
  security: Security;
  pageContext: PageContext;
  availableCash?: number;
  isLoading?: boolean;
  hasHoldings?: boolean;
}

interface TradingButtonsConfig {
  primary: ButtonConfig | null;
  secondary: ButtonConfig | null;
}

export function useTradingButtons(
  params?: UseTradingButtonsParams
): TradingButtonsConfig {
  const { t } = useModifiedTranslation();
  const tradingContext = useTradingContext();

  return useMemo(() => {
    // If no params provided, return empty config (for loading states)
    if (!params) {
      return { primary: null, secondary: null };
    }

    const { security, pageContext, availableCash, isLoading = false, hasHoldings = false } = params;
    const { tradingType, classification, financialProfile } = tradingContext;

    // No trading - no buttons
    if (tradingType === TradingType.NO_TRADING) {
      return { primary: null, secondary: null };
    }

    // Check if security is tradeable
    const isTradeable = isSecurityTradeable(security);
    
    // Special case: Not tradeable - show Call to Broker
    // Task.md: "Do not display Secondary button"
    if (!isTradeable) {
      return {
        primary: {
          action: ButtonAction.CALL_TO_BROKER,
          label: t("holdingsPage.callToBroker"),
          variant: ButtonVariant.LIGHT_GRAY,
          visible: true,
        },
        secondary: null,
      };
    }

    // Check for NON-UCITS securities (SecType:9 for retail)
    const isNonUCITS = isNonUCITSSecurity(classification, security);
    if (isNonUCITS) {
      // Show CALL_TO_BROKER on Portfolio and Security/Holding Details pages
      if (pageContext === PageContext.PORTFOLIO || pageContext === PageContext.SECURITY_DETAILS) {
        return {
          primary: {
            action: ButtonAction.CALL_TO_BROKER,
            label: t("holdingsPage.callToBroker"),
            variant: ButtonVariant.LIGHT_GRAY,
            visible: true,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
      // Don't show in trading list
      return { primary: null, secondary: null };
    }

    // Check for Professional-only securities
    const isProfOnly = isProfOnlySecurity(security);
    if (isProfOnly && tradingType !== TradingType.PROFESSIONAL_TRADING) {
      // Show CALL_TO_BROKER on Portfolio and Security/Holding Details pages
      if (pageContext === PageContext.PORTFOLIO || pageContext === PageContext.SECURITY_DETAILS) {
        return {
          primary: {
            action: ButtonAction.CALL_TO_BROKER,
            label: t("holdingsPage.callToBroker"),
            variant: ButtonVariant.LIGHT_GRAY,
            visible: true,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
      // Don't show in trading list
      return { primary: null, secondary: null };
    }

    // Check for securities without SecType tags
    // Task.md: "Do not display Secondary button"
    const needsCallForSecType = shouldShowCallToBrokerForSecType(security);
    if (needsCallForSecType) {
      // Show CALL_TO_BROKER on Portfolio and Security/Holding Details pages
      if (pageContext === PageContext.PORTFOLIO || pageContext === PageContext.SECURITY_DETAILS) {
        return {
          primary: {
            action: ButtonAction.CALL_TO_BROKER,
            label: t("holdingsPage.callToBroker"),
            variant: ButtonVariant.LIGHT_GRAY,
            visible: true,
          },
          secondary: null,
        };
      }
      // Don't show in trading list
      return { primary: null, secondary: null };
    }

    // Professional Trading
    if (tradingType === TradingType.PROFESSIONAL_TRADING) {
      return getRetailNonComplexTradingButtons(security, pageContext, availableCash, isLoading, hasHoldings, t);
    }

    // Retail Trading (both types)
    if (tradingType === TradingType.RETAIL_TRADING || tradingType === TradingType.RETAIL_NONCOMPLEX_TRADING) {
      return getRetailTradingButtons({
        security,
        pageContext,
        availableCash,
        isLoading,
        hasHoldings,
        tradingType,
        financialProfile,
        t,
      });
    }

    return { primary: null, secondary: null };
  }, [params, tradingContext, t]);
}

function getSellOrDetailsButton(pageContext: PageContext, hasHoldings: boolean, t: (key: string) => string): ButtonConfig | null {
  // Portfolio: always show SELL
  if (pageContext === PageContext.PORTFOLIO) {
    return {
      action: ButtonAction.SELL,
      label: t("holdingsPage.sellButton"),
      variant: ButtonVariant.RED,
      visible: true,
    };
  }
  
  // Security Details (includes Holding Details): show SELL only if user has holdings
  if (pageContext === PageContext.SECURITY_DETAILS) {
    if (hasHoldings) {
      return {
        action: ButtonAction.SELL,
        label: t("holdingsPage.sellButton"),
        variant: ButtonVariant.RED,
        visible: true,
      };
    }
    return null;
  }
  
  // TRADING context: no secondary button
  return null;
}

function getRetailNonComplexTradingButtons(
  security: Security,
  pageContext: PageContext,
  availableCash: number | undefined,
  isLoading: boolean,
  hasHoldings: boolean,
  t: (key: string) => string
): TradingButtonsConfig {
  const hasCash = hasEnoughCash(availableCash);
  const isSubscription = hasSecurityTag(security, SUBSCR_PORTAL_GROUP);

  // Subscription securities
  if (isSubscription) {
    if (pageContext === PageContext.SECURITY_DETAILS) {
      return {
        primary: hasCash ? {
          action: ButtonAction.SUBSCRIPTION,
          label: t("tradingList.subscribeButton"),
          variant: ButtonVariant.SUCCESS,
          visible: true,
        } : {
          action: ButtonAction.EXCHANGE,
          label: t("exchange"),
          variant: ButtonVariant.RED,
          visible: true,
          isLoading,
        },
        secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
      };
    }
    // Portfolio page: show SUBSCRIPTION/EXCHANGE with SELL button
    if (pageContext === PageContext.PORTFOLIO) {
      return {
        primary: hasCash ? {
          action: ButtonAction.SUBSCRIPTION,
          label: t("tradingList.subscribeButton"),
          variant: ButtonVariant.SUCCESS,
          visible: true,
        } : {
          action: ButtonAction.EXCHANGE,
          label: t("exchange"),
          variant: ButtonVariant.RED,
          visible: true,
          isLoading,
        },
        secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
      };
    }
    // For TRADING context, subscription securities don't show any buttons
    return {
      primary: null,
      secondary: null,
    };
  }

  // Regular securities
  const primary: ButtonConfig = hasCash ? {
    action: ButtonAction.BUY,
    label: t("holdingsPage.buyButton"),
    variant: ButtonVariant.SUCCESS,
    visible: true,
  } : {
    action: ButtonAction.EXCHANGE,
    label: t("exchange"),
    variant: ButtonVariant.RED,
    visible: true,
    isLoading,
  };

  const secondary = getSellOrDetailsButton(pageContext, hasHoldings, t);

  return { primary, secondary };
}

function getRetailTradingButtons(params: {
  security: Security;
  pageContext: PageContext;
  availableCash: number | undefined;
  isLoading: boolean;
  hasHoldings: boolean;
  tradingType: TradingType.RETAIL_TRADING | TradingType.RETAIL_NONCOMPLEX_TRADING;
  financialProfile?: {
    contacts?: Array<{
      profile?: {
        etfExpiryDate?: { dateValue?: string | null };
        derivativesExpiryDate?: { dateValue?: string | null };
        riskUnderstExpiryDate?: { dateValue?: string | null };
      };
    }>;
  };
  t: (key: string) => string;
}): TradingButtonsConfig {
  const { security, pageContext, availableCash, isLoading, hasHoldings, tradingType, financialProfile, t } = params;
  
  const hasCash = hasEnoughCash(availableCash);
  const isComplex = isSecurityComplex(security);
  const isNonComplex = isSecurityNonComplex(security);
  const isSubscription = hasSecurityTag(security, SUBSCR_PORTAL_GROUP);

  // For TRADING context, subscription securities don't show any buttons
  // (Details button is handled separately in trading list components)
  if (isSubscription && pageContext === PageContext.TRADING) {
    return {
      primary: null,
      secondary: null,
    };
  }

  // Non-complex securities
  if (isNonComplex) {
    // Non-complex subscriptions: no test needed
    if (isSubscription) {
      return {
        primary: hasCash ? {
          action: ButtonAction.SUBSCRIPTION,
          label: t("tradingList.subscribeButton"),
          variant: ButtonVariant.SUCCESS,
          visible: true,
        } : {
          action: ButtonAction.EXCHANGE,
          label: t("exchange"),
          variant: ButtonVariant.RED,
          visible: true,
          isLoading,
        },
        secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
      };
    }

    // Regular non-complex securities
    const primary: ButtonConfig = hasCash ? {
      action: ButtonAction.BUY,
      label: t("holdingsPage.buyButton"),
      variant: ButtonVariant.SUCCESS,
      visible: true,
    } : {
      action: ButtonAction.EXCHANGE,
      label: t("exchange"),
      variant: ButtonVariant.RED,
      visible: true,
      isLoading,
    };

    const secondary = getSellOrDetailsButton(pageContext, hasHoldings, t);
    return { primary, secondary };
  }

  // Complex securities
  if (isComplex) {
    // Retail non-complex trading can't trade complex
    if (tradingType === TradingType.RETAIL_NONCOMPLEX_TRADING) {
      if (pageContext === PageContext.PORTFOLIO || pageContext === PageContext.SECURITY_DETAILS) {
        return {
          primary: {
            action: ButtonAction.CALL_TO_BROKER,
            label: t("holdingsPage.callToBroker"),
            variant: ButtonVariant.LIGHT_GRAY,
            visible: true,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
      // Don't show in trading list
      return { primary: null, secondary: null };
    }

    // Retail trading with complex - pass subscription flag to check appropriateness tests
    return getComplexSecurityButtons(security, pageContext, availableCash, isLoading, hasHoldings, financialProfile, isSubscription, t);
  }

  return { primary: null, secondary: null };
}

function getComplexSecurityButtons(
  security: Security,
  pageContext: PageContext,
  availableCash: number | undefined,
  isLoading: boolean,
  hasHoldings: boolean,
  financialProfile: {
    contacts?: Array<{
      profile?: {
        etfExpiryDate?: { dateValue?: string | null };
        derivativesExpiryDate?: { dateValue?: string | null };
        riskUnderstExpiryDate?: { dateValue?: string | null };
      };
    }>;
  } | undefined,
  isSubscription: boolean,
  t: (key: string) => string
): TradingButtonsConfig {
  const hasCash = hasEnoughCash(availableCash);
  const secType = security.securityTypeCode;
  
  // Determine the correct action and label based on subscription status
  const buyAction = isSubscription ? ButtonAction.SUBSCRIPTION : ButtonAction.BUY;
  const buyLabel = isSubscription ? t("tradingList.subscribeButton") : t("holdingsPage.buyButton");

  // ETF (CE) complex
  if (secType === SecurityTypeCode.ETF || secType === SecurityTypeCode.EQUITY) {
    const etfExpiry = financialProfile?.contacts?.[0]?.profile?.etfExpiryDate?.dateValue;
    const isTestValid = isFutureOrTodayDate(etfExpiry);

    if (isTestValid) {
      return {
        primary: hasCash ? {
          action: buyAction,
          label: buyLabel,
          variant: ButtonVariant.SUCCESS,
          visible: true,
        } : {
          action: ButtonAction.EXCHANGE,
          label: t("exchange"),
          variant: ButtonVariant.RED,
          visible: true,
          isLoading,
        },
        secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
      };
    }

    return {
      primary: {
        action: ButtonAction.FILL_THE_TEST,
        label: t("holdingsPage.fillTheTest"),
        variant: ButtonVariant.PRIMARY,
        visible: true,
      },
      secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
    };
  }

  // Debt instruments (D) complex
  if (secType === SecurityTypeCode.DEBT_INSTRUMENT) {
    const hasSecType10 = hasSecurityTag(security, SEC_TYPE_10);
    const hasSecType11 = hasSecurityTag(security, SEC_TYPE_11);

    const derivativesExpiry = financialProfile?.contacts?.[0]?.profile?.derivativesExpiryDate?.dateValue;
    const riskExpiry = financialProfile?.contacts?.[0]?.profile?.riskUnderstExpiryDate?.dateValue;

    const isDerivativesTestValid = isFutureOrTodayDate(derivativesExpiry);
    const isRiskTestValid = isFutureOrTodayDate(riskExpiry);

    // SecType:10 only
    if (hasSecType10 && !hasSecType11) {
      if (isDerivativesTestValid) {
        return {
          primary: hasCash ? {
            action: buyAction,
            label: buyLabel,
            variant: ButtonVariant.SUCCESS,
            visible: true,
          } : {
            action: ButtonAction.EXCHANGE,
            label: t("exchange"),
            variant: ButtonVariant.RED,
            visible: true,
            isLoading,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
    }

    // SecType:11 only
    if (!hasSecType10 && hasSecType11) {
      if (isRiskTestValid) {
        return {
          primary: hasCash ? {
            action: buyAction,
            label: buyLabel,
            variant: ButtonVariant.SUCCESS,
            visible: true,
          } : {
            action: ButtonAction.EXCHANGE,
            label: t("exchange"),
            variant: ButtonVariant.RED,
            visible: true,
            isLoading,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
    }

    // Both SecType:10 and SecType:11
    if (hasSecType10 && hasSecType11) {
      if (isDerivativesTestValid && isRiskTestValid) {
        return {
          primary: hasCash ? {
            action: buyAction,
            label: buyLabel,
            variant: ButtonVariant.SUCCESS,
            visible: true,
          } : {
            action: ButtonAction.EXCHANGE,
            label: t("exchange"),
            variant: ButtonVariant.RED,
            visible: true,
            isLoading,
          },
          secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
        };
      }
    }

    // If no valid tests
    return {
      primary: {
        action: ButtonAction.FILL_THE_TEST,
        label: t("holdingsPage.fillTheTest"),
        variant: ButtonVariant.PRIMARY,
        visible: true,
      },
      secondary: getSellOrDetailsButton(pageContext, hasHoldings, t),
    };
  }

  return { primary: null, secondary: null };
}
